import React, { useState } from 'react';
import { ExternalLink, ChevronDown, ChevronRight } from 'lucide-react';
import { components, typography, palette, spacing, withOpacity } from '../utils/designTokens';

// Open source packages used in this application
const packages = [
  {
    name: 'React',
    version: '18.2.0',
    license: 'MIT',
    repository: 'https://github.com/facebook/react',
    copyright: 'Copyright (c) Meta Platforms, Inc. and affiliates.',
    description: 'A JavaScript library for building user interfaces'
  },
  {
    name: 'React Router',
    version: '6.8.0',
    license: 'MIT',
    repository: 'https://github.com/remix-run/react-router',
    copyright: 'Copyright (c) React Training LLC 2015-2019, Remix Software Inc. 2020-2024',
    description: 'Declarative routing for React applications'
  },
  {
    name: 'Apollo Client',
    version: '3.8.0',
    license: 'MIT',
    repository: 'https://github.com/apollographql/apollo-client',
    copyright: 'Copyright (c) 2022 Apollo Graph, Inc.',
    description: 'A fully-featured GraphQL client'
  },
  {
    name: 'Stytch React SDK',
    version: '19.9.0',
    license: 'MIT',
    repository: 'https://github.com/stytchauth/stytch-js',
    copyright: 'Copyright (c) Stytch',
    description: 'Authentication SDK for React applications'
  },
  {
    name: 'Tailwind CSS',
    version: '3.3.0',
    license: 'MIT',
    repository: 'https://github.com/tailwindlabs/tailwindcss',
    copyright: 'Copyright (c) Tailwind Labs, Inc.',
    description: 'A utility-first CSS framework'
  },
  {
    name: 'Headless UI',
    version: '1.7.17',
    license: 'MIT',
    repository: 'https://github.com/tailwindlabs/headlessui',
    copyright: 'Copyright (c) Tailwind Labs, Inc.',
    description: 'Unstyled, accessible UI components'
  },
  {
    name: 'Lucide React',
    version: '0.263.1',
    license: 'ISC',
    repository: 'https://github.com/lucide-icons/lucide',
    copyright: 'Copyright (c) Lucide Contributors',
    description: 'Beautiful & consistent icon toolkit'
  },
  {
    name: 'React Flow',
    version: '11.11.4',
    license: 'MIT',
    repository: 'https://github.com/xyflow/xyflow',
    copyright: 'Copyright (c) 2019-2024 webkid GmbH',
    description: 'Library for building node-based graphs and diagrams'
  },
  {
    name: 'D3 Force',
    version: '3.0.0',
    license: 'ISC',
    repository: 'https://github.com/d3/d3-force',
    copyright: 'Copyright 2010-2024 Mike Bostock',
    description: 'Force-directed graph layout using velocity Verlet integration'
  },
  {
    name: 'Dagre',
    version: '0.8.5',
    license: 'MIT',
    repository: 'https://github.com/dagrejs/dagre',
    copyright: 'Copyright (c) 2012-2014 Chris Pettitt',
    description: 'Directed graph layout for JavaScript'
  },
  {
    name: 'date-fns',
    version: '2.30.0',
    license: 'MIT',
    repository: 'https://github.com/date-fns/date-fns',
    copyright: 'Copyright (c) 2021 Sasha Koss and Lesha Koss',
    description: 'Modern JavaScript date utility library'
  },
  {
    name: 'GraphQL',
    version: '16.8.0',
    license: 'MIT',
    repository: 'https://github.com/graphql/graphql-js',
    copyright: 'Copyright (c) GraphQL Contributors',
    description: 'The JavaScript reference implementation for GraphQL'
  },
  {
    name: 'clsx',
    version: '2.0.0',
    license: 'MIT',
    repository: 'https://github.com/lukeed/clsx',
    copyright: 'Copyright (c) Luke Edwards',
    description: 'Utility for constructing className strings conditionally'
  },
  {
    name: 'React Markdown',
    version: '9.0.1',
    license: 'MIT',
    repository: 'https://github.com/remarkjs/react-markdown',
    copyright: 'Copyright (c) Espen Hovlandsdal',
    description: 'Markdown component for React'
  },
  {
    name: 'React Window',
    version: '1.8.9',
    license: 'MIT',
    repository: 'https://github.com/bvaughn/react-window',
    copyright: 'Copyright (c) 2018 Brian Vaughn',
    description: 'React components for efficiently rendering large lists'
  },
  {
    name: 'React JSON Tree',
    version: '0.20.0',
    license: 'MIT',
    repository: 'https://github.com/reduxjs/redux-devtools',
    copyright: 'Copyright (c) 2015 Dan Abramov',
    description: 'React component for displaying JSON data as a tree'
  },
  {
    name: 'React Highlight Words',
    version: '0.21.0',
    license: 'MIT',
    repository: 'https://github.com/bvaughn/react-highlight-words',
    copyright: 'Copyright (c) 2015 Brian Vaughn',
    description: 'React component to highlight words within text'
  },
  {
    name: 'Force Graph',
    version: '1.50.1',
    license: 'MIT',
    repository: 'https://github.com/vasturiano/force-graph',
    copyright: 'Copyright (c) 2018 Vasco Asturiano',
    description: 'Force-directed graph rendered on HTML5 canvas'
  }
];

const licenseTexts = {
  MIT: `MIT License

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`,
  ISC: `ISC License

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.`
};

const LicenseCard = ({ pkg }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`${components.card.bg} ${components.card.rounded} border`}
      style={{ borderColor: components.card.borderColor }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-start justify-between text-left hover:bg-white/5 transition-colors"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h3 className={`${typography.size.base} ${typography.weight.semibold}`} style={{ color: palette.text.primary }}>
              {pkg.name}
            </h3>
            <span className={`${typography.size.xs} px-2 py-0.5 rounded`} style={{ backgroundColor: withOpacity(palette.accents.primary.base, 20), color: palette.accents.primary.base }}>
              {pkg.license}
            </span>
            <span className={`${typography.size.xs}`} style={{ color: palette.text.muted }}>
              v{pkg.version}
            </span>
          </div>
          <p className={`${typography.size.sm}`} style={{ color: palette.text.supporting }}>
            {pkg.description}
          </p>
        </div>
        <div className="ml-4 flex items-center gap-2">
          <a
            href={pkg.repository}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="p-1 hover:bg-white/10 rounded"
            title="View repository"
          >
            <ExternalLink size={16} style={{ color: palette.text.muted }} />
          </a>
          {expanded ? (
            <ChevronDown size={20} style={{ color: palette.text.muted }} />
          ) : (
            <ChevronRight size={20} style={{ color: palette.text.muted }} />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: components.card.borderColor }}>
          <div className="pt-4">
            <p className={`${typography.size.sm} mb-3`} style={{ color: palette.text.supporting }}>
              {pkg.copyright}
            </p>
            <pre
              className={`${typography.size.xs} p-3 rounded overflow-x-auto`}
              style={{ backgroundColor: palette.neutrals.surfaceSubtle, color: palette.text.muted, whiteSpace: 'pre-wrap' }}
            >
              {licenseTexts[pkg.license] || `Licensed under ${pkg.license}`}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

const Licenses = () => {
  const licenseCounts = packages.reduce((acc, pkg) => {
    acc[pkg.license] = (acc[pkg.license] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className={`min-h-screen p-8`} style={{ backgroundColor: palette.neutrals.canvas }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`${typography.size['2xl']} ${typography.weight.bold} mb-2`} style={{ color: palette.text.primary }}>
            Open Source Licenses
          </h1>
          <p className={`${typography.size.base}`} style={{ color: palette.text.supporting }}>
            Lumoz Console is built with the help of the following open source software.
            We are grateful to the developers and maintainers of these projects.
          </p>
        </div>

        {/* Summary */}
        <div className={`${components.card.bg} ${components.card.rounded} ${components.card.padding} mb-8 border`} style={{ borderColor: components.card.borderColor }}>
          <h2 className={`${typography.size.sm} ${typography.weight.semibold} uppercase tracking-wide mb-4`} style={{ color: palette.text.neutral }}>
            License Summary
          </h2>
          <div className="flex gap-6">
            {Object.entries(licenseCounts).map(([license, count]) => (
              <div key={license} className="flex items-center gap-2">
                <span className={`${typography.size.xs} px-2 py-0.5 rounded`} style={{ backgroundColor: withOpacity(palette.accents.primary.base, 20), color: palette.accents.primary.base }}>
                  {license}
                </span>
                <span className={`${typography.size.sm}`} style={{ color: palette.text.muted }}>
                  {count} {count === 1 ? 'package' : 'packages'}
                </span>
              </div>
            ))}
          </div>
          <p className={`${typography.size.sm} mt-4`} style={{ color: palette.text.muted }}>
            Total: {packages.length} packages (showing direct dependencies only, {'>'}200 including transitive dependencies)
          </p>
        </div>

        {/* Package List */}
        <div className={`flex flex-col ${spacing.gap.md}`}>
          {packages.map((pkg) => (
            <LicenseCard key={pkg.name} pkg={pkg} />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-8 border-t" style={{ borderColor: components.card.borderColor }}>
          <p className={`${typography.size.sm}`} style={{ color: palette.text.muted }}>
            This page lists the primary open source packages used in the Lumoz Console.
            For a complete list including all transitive dependencies, please refer to our{' '}
            <a
              href="https://github.com/lumozai/mvp"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: palette.accents.primary.base }}
              className="hover:underline"
            >
              package.json
            </a>{' '}
            and run <code className="px-1 py-0.5 rounded" style={{ backgroundColor: palette.neutrals.surfaceSubtle }}>npx license-checker</code>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Licenses;
